<script setup lang="ts">
import { ref, computed, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldPart } from "@/types/field-observations.types";
import { useGetFieldParts } from "@/stores/queries/field-observations.query";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import PartsTreePanel from "./PartsTreePanel.vue";

/**
 * Sélecteur de partie compact : une ligne (breadcrumb du chemin complet), au
 * clic une modal avec l'arborescence complète (`PartsTreePanel` : ajout,
 * renommage, insertion de modèle). Choisir une partie referme la modal.
 */

const props = defineProps<{
  diagnosticId: number;
  modelValue?: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number | undefined): void;
}>();

const { t } = useTranslation();
const { data: parts } = useGetFieldParts(toRef(props, "diagnosticId"));

const showDialog = ref(false);

/** Chemin complet (racine → partie) de la sélection, pour le breadcrumb. */
const selectedPath = computed<FieldPart[]>(() => {
  if (!props.modelValue || !parts.value) return [];
  const path: FieldPart[] = [];
  let current = parts.value.find((p) => p.id === props.modelValue);
  while (current) {
    path.unshift(current);
    current = current.parentId
      ? parts.value.find((p) => p.id === current!.parentId)
      : undefined;
  }
  return path;
});

const selectPart = (partId: number | undefined) => {
  emit("update:modelValue", partId);
  showDialog.value = false;
};

const clearPart = () => {
  emit("update:modelValue", undefined);
};
</script>

<template>
  <!-- Ligne compacte : breadcrumb ou placeholder -->
  <div
    class="part-line d-flex align-items-center gap-2 rounded px-2 py-1"
    role="button"
    @click="showDialog = true"
  >
    <div v-if="selectedPath.length" class="breadcrumb-path flex-grow-1">
      <template v-for="(part, i) in selectedPath" :key="part.id">
        <span v-if="i > 0" class="separator">/</span>
        <span :class="{ 'fw-medium': i === selectedPath.length - 1 }">
          {{ part.name }}
        </span>
      </template>
    </div>
    <span v-else class="placeholder-text flex-grow-1">
      {{ t("canvas:observations.part.placeholder") }}
    </span>
    <DRBtnIcon
      v-if="selectedPath.length"
      variant="flat"
      :size="20"
      phosphor-icon="x"
      @click.stop="clearPart"
    />
    <PhosphorIcon
      v-else
      name="caret-right"
      :size="12"
      weight="bold"
      class="flex-shrink-0 text-secondary"
    />
  </div>

  <!-- Modal arborescence complète -->
  <!-- Ce sélecteur ne vit que dans l'outil dessin : modal aux couleurs du canvas -->
  <DRDialog
    v-model="showDialog"
    class="canvas-dialog"
    :title="t('dashboard:fieldObservations.part.label')"
    hide-footer
  >
    <PartsTreePanel
      :diagnostic-id="diagnosticId"
      :model-value="modelValue"
      @update:model-value="selectPart"
    />
  </DRDialog>
</template>

<style lang="scss" scoped>
.part-line {
  background-color: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  cursor: pointer;
  min-height: 32px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: var(--bs-primary);
    }
  }
}

.breadcrumb-path {
  font-size: 0.8rem;
  line-height: 1.3;

  .separator {
    margin: 0 4px;
    color: var(--bs-secondary-color);
  }
}

.placeholder-text {
  font-size: 0.8rem;
  color: var(--bs-secondary-color);
}
</style>
